import React from "react";
import { Habit, Category } from "../types";
import { IOSToggle } from "../../../components/IOSToggle";

interface HabitModalProps {
  isOpen: boolean;
  isEdit: boolean;
  habit: Habit | null;
  categories: Category[] | undefined;
  categoriesEnabled: boolean;
  onSave: () => void;
  onCancel: () => void;
  onArchive: (id: number) => void;
  onDelete: (id: number) => void;
  onHabitChange: (habit: Habit) => void;
}

export const HabitModal: React.FC<HabitModalProps> = ({
  isOpen,
  isEdit,
  habit,
  categories,
  categoriesEnabled,
  onSave,
  onCancel,
  onArchive,
  onDelete,
  onHabitChange,
}) => {
  if (!isOpen || !habit) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        animation: "fadeIn 0.1s ease-out forwards",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          width: "90%",
          maxWidth: "400px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{
          padding: "24px 24px 16px 24px",
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}>
          <h3 style={{
            fontSize: "20px",
            fontWeight: 600,
            margin: 0,
            color: "#000"
          }}>
            {isEdit ? "Edit Habit" : "Add Habit"}
          </h3>
        </div>

        <div style={{
          padding: "24px",
          overflowY: "auto",
          flex: 1,
          position: "relative",
        }}>
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "20px",
            background: `linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))`,
            pointerEvents: "none",
          }} />
          <div style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "20px",
            background: `linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))`,
            pointerEvents: "none",
          }} />

          <input
            type="text"
            placeholder="Name"
            value={habit.name || ""}
            onChange={(e) => onHabitChange({ ...habit, name: e.target.value })}
            style={{
              width: "100%",
              padding: "12px 16px",
              marginBottom: "16px",
              borderRadius: "12px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              color: "#000",
              fontSize: "16px",
              outline: "none",
            }}
          />

          <input
            type="number"
            placeholder="Score"
            value={habit.score || ""}
            onChange={(e) => onHabitChange({ ...habit, score: parseInt(e.target.value) })}
            style={{
              width: "100%",
              padding: "12px 16px",
              marginBottom: "24px",
              borderRadius: "12px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              color: "#000",
              fontSize: "16px",
              outline: "none",
            }}
          />

          <textarea
            placeholder="Note (optional)"
            value={habit.note || ""}
            onChange={(e) => onHabitChange({ ...habit, note: e.target.value })}
            style={{
              width: "100%",
              padding: "12px 16px",
              marginBottom: "24px",
              borderRadius: "12px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              color: "#000",
              fontSize: "16px",
              minHeight: "80px",
              resize: "vertical",
              outline: "none",
            }}
          />

          {categoriesEnabled && categories && categories.length > 0 && (
            <select
              value={habit.categoryId || ""}
              onChange={(e) => onHabitChange({ ...habit, categoryId: e.target.value ? parseInt(e.target.value) : undefined })}
              style={{
                width: "100%",
                padding: "12px 16px",
                marginBottom: "24px",
                borderRadius: "12px",
                border: "1px solid #ccc",
                backgroundColor: "#fff",
                color: "#000",
                fontSize: "16px",
                outline: "none",
              }}
            >
              <option value="">No Category</option>
              {categories?.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          )}

          <div style={{
            marginBottom: "24px",
            display: "flex",
            flexDirection: "column",
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 0",
            }}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: "4px" }}>Daily Streak</div>
                <div style={{ fontSize: "0.85em", color: "rgba(0,0,0,0.6)" }}>
                  Track consecutive days
                </div>
              </div>
              <IOSToggle
                isOn={habit.streakEnabled || false}
                onChange={(value) => onHabitChange({ ...habit, streakEnabled: value })}
              />
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 0",
              opacity: habit.streakEnabled ? 1 : 0.5
            }}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: "4px" }}>Allow Skip</div>
                <div style={{ fontSize: "0.85em", color: "rgba(0,0,0,0.6)" }}>
                  Skip 1 day per week
                </div>
              </div>
              <IOSToggle
                isOn={habit.allowSkip || false}
                onChange={(value) => onHabitChange({ ...habit, allowSkip: value })}
                disabled={!habit.streakEnabled}
              />
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 0",
              opacity: habit.streakEnabled ? 1 : 0.5
            }}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: "4px" }}>Weekly Bonus</div>
                <div style={{ fontSize: "0.85em", color: "rgba(0,0,0,0.6)" }}>
                  Double points for week-long streaks
                </div>
              </div>
              <IOSToggle
                isOn={habit.weeklyBonus || false}
                onChange={(value) => onHabitChange({ ...habit, weeklyBonus: value })}
                disabled={!habit.streakEnabled}
              />
            </div>

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 0",
            }}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: "4px" }}>Track Progress</div>
                <div style={{ fontSize: "0.85em", color: "rgba(0,0,0,0.6)" }}>
                  Track count, duration, etc.
                </div>
              </div>
              <IOSToggle
                isOn={habit.progressTracking || false}
                onChange={(value) => onHabitChange({ ...habit, progressTracking: value })}
                data-testid="progress-tracking-toggle"
              />
            </div>

            {habit.progressTracking && (
              <div style={{ marginTop: "8px" }}>
                <input
                  type="text"
                  placeholder="Progress Unit (e.g., minutes, pages, km)"
                  value={habit.progressUnit || ""}
                  onChange={(e) => onHabitChange({ ...habit, progressUnit: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    marginTop: "12px",
                    borderRadius: "12px",
                    border: "1px solid #ccc",
                    backgroundColor: "#fff",
                    color: "#000",
                    fontSize: "16px",
                    outline: "none",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div style={{
          padding: "24px",
          borderTop: "1px solid rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
          borderRadius: "0 0 16px 16px",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {isEdit && (
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => onArchive(habit.id)}
                  style={{
                    flex: 1,
                    padding: "14px",
                    backgroundColor: habit.archived ? "#28a745" : "#ffc107",
                    color: "#fff",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  {habit.archived ? "Unarchive" : "Archive"}
                </button>
                {habit.archived && (
                  <button
                    onClick={() => onDelete(habit.id)}
                    style={{
                      flex: 1,
                      padding: "14px",
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={onCancel}
                style={{
                  flex: 1,
                  padding: "14px",
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={!habit.name || habit.score === undefined}
                style={{
                  flex: 1,
                  padding: "14px",
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  cursor: (!habit.name || habit.score === undefined) ? "not-allowed" : "pointer",
                  opacity: (!habit.name || habit.score === undefined) ? 0.5 : 1,
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};